import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronUp } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";

const LAST_UPDATED = "2026-04-22";

const sections = [
  { id: "overview", title: "Overview" },
  { id: "free-local", title: "Free local plugin mode" },
  { id: "pairing", title: "Pairing and telemetry" },
  { id: "managed", title: "Managed and active-plan behaviour" },
  { id: "diagnostics", title: "Diagnostics and optional external services" },
  { id: "responsibilities", title: "User responsibilities" },
  { id: "limits", title: "Plugin limits and disclaimers" },
  { id: "privacy", title: "Privacy and data handling" },
  { id: "law", title: "Governing law" },
  { id: "contact", title: "Contact" },
];

const chips = [
  "Free local mode available",
  "Pairing is optional",
  "Managed features stay gated",
  "Remote diagnostics is opt-in",
];

export default function PluginAddendum() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const hash = location.hash;
  const hashId = hash ? hash.replace("#", "") : "";
  const activeId = sections.some((section) => section.id === hashId) ? hashId : "overview";

  const scrollToSection = (id: string, updateHash = true) => {
    if (typeof document === "undefined") return;
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    if (updateHash && location.hash !== `#${id}`) {
      navigate({ pathname: location.pathname, hash: `#${id}` }, { replace: false });
    }
  };

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    if (!id) return;
    const element = document.getElementById(id);
    if (!element) return;
    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hash]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => setShowBackToTop(window.scrollY > 300);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>ShadowScan Plugin Addendum</title>
        <meta
          name="description"
          content="ShadowScan Plugin Addendum covering free local plugin use, paired telemetry, managed features, remote diagnostics, and plugin-specific limits."
        />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24" id="top">
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// PLUGIN_ADDENDUM</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">ShadowScan Plugin Addendum</h1>
              <p className="text-sm text-muted-foreground mb-4">
                This Addendum applies to the ShadowScan WordPress plugin in both free local mode and paired or managed mode. It supplements the <Link className="text-primary hover:underline" to="/terms">Terms of Service</Link> and the <Link className="text-primary hover:underline" to="/privacy">Privacy Policy</Link>.
              </p>
              <p className="text-xs text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto rounded-xl border border-border/60 bg-secondary/20 p-6 md:p-8 space-y-4 text-sm text-muted-foreground">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <h2 className="text-lg font-heading font-semibold text-foreground">
                  Plain-English plugin summary
                </h2>
                <div className="flex flex-wrap gap-2">
                  {chips.map((chip) => (
                    <span
                      key={chip}
                      className="px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs text-primary"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
              <ul className="list-disc pl-6 space-y-2">
                <li>The plugin can be used locally without pairing for WordPress hardening, diagnostics, and security controls available on the installed version.</li>
                <li>Pairing the plugin to ShadowScan is optional, but required for managed features, heartbeat sync, command polling, and active-plan workflows.</li>
                <li>The plugin does not submit WordPress administrator passwords to ShadowScan through pairing or routine heartbeat flows.</li>
                <li>Optional remote diagnostics and the temporary MU helper are available only when an authorised administrator enables the relevant settings and prerequisites are met.</li>
                <li>Plugin behaviour remains subject to your hosting environment, WordPress compatibility, other plugins, and your own change-control and backup practices.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
              <aside className="space-y-4">
                <div className="rounded-xl border border-border/60 bg-secondary/20 p-5 hidden lg:block sticky top-28">
                  <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Sections</h3>
                  <nav>
                    <ul className="space-y-3 text-sm">
                      {sections.map((section) => {
                        const isActive = activeId === section.id;
                        return (
                          <li key={section.id}>
                            <button
                              className={`w-full text-left transition-colors ${
                                isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                              }`}
                              onClick={() => scrollToSection(section.id)}
                              type="button"
                            >
                              {section.title}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                </div>

                <div className="lg:hidden">
                  <label htmlFor="plugin-addendum-sections" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Sections
                  </label>
                  <select
                    id="plugin-addendum-sections"
                    className="mt-2 w-full rounded-lg border border-border/60 bg-secondary/30 px-4 py-3 text-sm text-foreground"
                    value={activeId}
                    onChange={(event) => scrollToSection(event.target.value)}
                  >
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.title}
                      </option>
                    ))}
                  </select>
                </div>
              </aside>

              <div className="space-y-10 text-sm text-muted-foreground leading-relaxed">
                <section id="overview" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">1. Overview</h2>
                  <p>
                    This Addendum applies specifically to the ShadowScan WordPress plugin (the “Plugin”). It supplements the <Link className="text-primary hover:underline" to="/terms">Terms of Service</Link>. If there is any inconsistency, this Addendum controls for plugin-specific behaviour and disclosures.
                  </p>
                  <p>
                    The Plugin runs in a WordPress environment that may include third-party plugins, shared hosting restrictions, and varying server controls. ShadowScan designs the Plugin to reduce risk and surface useful security information, but plugin behaviour may still be affected by the surrounding environment.
                  </p>
                </section>

                <section id="free-local" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">2. Free local plugin mode</h2>
                  <p>
                    The Plugin may be used locally without pairing to ShadowScan. In free local mode, available features are limited to the controls, diagnostics, and local security workflows provided by the installed plugin version.
                  </p>
                  <p>
                    Free local mode does not by itself create an entitlement to managed services, portal monitoring, remote actions, or human response. Those services depend on separate pairing, account, and plan status requirements where offered.
                  </p>
                </section>

                <section id="pairing" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">3. Pairing and telemetry</h2>
                  <p>
                    Pairing is an administrator-initiated action that links the Plugin to a backend service using a pairing code and a site token issued for that purpose. The Plugin supports dual backend pairing with both Guard and ShadowScan backends. Once paired, the Plugin may send heartbeat, status, and command-related service data needed to operate the connection.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Guard pairing codes use XX-XX-XX format and connect to the Guard backend for Guard-specific features.</li>
                    <li>ShadowScan pairing codes use 8-12 character format and connect to the ShadowScan backend for portal-managed features.</li>
                    <li>Migration tokens use XXXX-XXXX-XXXX-XXXX format and enable backend switching from Guard to ShadowScan.</li>
                    <li>Routine paired telemetry may include site URL, WordPress version, PHP version, plugin version, control status, heartbeat timing, and command execution metadata.</li>
                    <li>Deactivating the Plugin does not necessarily disconnect the site from the backend. Disconnect behaviour depends on the explicit disconnect or uninstall flow described in the plugin documentation.</li>
                    <li>The Plugin does not submit WordPress administrator passwords to any backend through pairing or routine heartbeat flows.</li>
                  </ul>
                </section>

                <section id="managed" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">4. Managed and active-plan behaviour</h2>
                  <p>
                    Some Plugin features depend on an active ShadowScan Portal connection and, where applicable, an active paid plan. Managed or portal-driven functionality may include command polling, connection-health reporting, support workflows, and other features specifically gated by plan status.
                  </p>
                  <p>
                    If you use the Plugin as part of a managed ShadowScan service, the managed service agreement, accepted pricing schedule, this Addendum, the Terms, and the Privacy Policy form part of the overall document set. More specific plugin wording in this Addendum controls for plugin-specific behaviour.
                  </p>
                </section>

                <section id="diagnostics" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">5. Diagnostics and optional external services</h2>
                  <p>
                    Certain Plugin features rely on optional external services or temporary troubleshooting components that are enabled only when an authorised administrator chooses to use them.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Optional breached-password checks use the Have I Been Pwned Passwords API with k-anonymity hash-prefix requests only.</li>
                    <li>Optional Sentry telemetry is disabled by default and only sends error-event metadata after explicit administrator opt-in and required configuration.</li>
                    <li>Optional remote diagnostics may install or prepare a temporary must-use helper for troubleshooting while diagnostics is enabled and the workflow requires it.</li>
                  </ul>
                  <p>
                    Remote diagnostics, MU helper activity, and related troubleshooting steps may affect logs, error capture, or plugin state. They are intended to assist investigation and should be enabled only for systems you are authorised to administer and only while needed.
                  </p>
                </section>

                <section id="responsibilities" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">6. User responsibilities</h2>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You must install, activate, pair, configure, and use the Plugin only on systems you own or are authorised to administer.</li>
                    <li>You remain responsible for backups, hosting access, compatibility checks, and validating business-critical functionality after changes.</li>
                    <li>You must not use the Plugin, any ShadowScan-connected workflow, or any associated guidance to facilitate unauthorised access, exploitation, or disruption.</li>
                    <li>If you enable managed or diagnostic workflows, you remain responsible for assessing operational impact and for obtaining any approvals required inside your organisation or from your client.</li>
                  </ul>
                </section>

                <section id="limits" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">7. Plugin limits and disclaimers</h2>
                  <p>
                    The Plugin is provided on an “as is” and “as available” basis to the maximum extent permitted by law. WordPress environments vary widely, and the Plugin may be affected by incompatible themes, plugins, hosting restrictions, caching layers, file-permission issues, or partial failures.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>ShadowScan does not guarantee that the Plugin will detect every issue, remain compatible with every environment, or execute every requested action successfully.</li>
                    <li>Remote or automated actions may fail partially, require manual follow-up, or have operational side effects.</li>
                    <li>Nothing in this Addendum excludes rights or remedies that cannot be excluded under the Australian Consumer Law.</li>
                  </ul>
                </section>

                <section id="privacy" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">8. Privacy and data handling</h2>
                  <p>
                    Plugin data handling is described in the <Link className="text-primary hover:underline" to="/privacy">Privacy Policy</Link>, including plugin telemetry, overseas disclosure, subprocessors, optional Sentry telemetry, and Have I Been Pwned password-breach checks.
                  </p>
                  <p>
                    If you use the Plugin in paired mode, you agree that ShadowScan may process the limited service data needed to operate that connection, store audit or acceptance metadata associated with plugin checkpoints, and retain related records for operational, security, or legal purposes.
                  </p>
                </section>

                <section id="law" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">9. Governing law</h2>
                  <p>
                    This Addendum is governed by the laws of New South Wales, Australia. You and ShadowScan submit to the non-exclusive jurisdiction of the courts of New South Wales and the Commonwealth courts competent to hear appeals from those courts.
                  </p>
                </section>

                <section id="contact" className="scroll-mt-28 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">10. Contact</h2>
                  <p>Questions about this Addendum can be sent to ShadowScan, operated by Valnex Holdings.</p>
                  <p>
                    Email: <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>
        <button
          className={`fixed bottom-6 right-6 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-secondary/80 text-foreground shadow-lg backdrop-blur transition duration-200 hover:text-primary ${
            showBackToTop ? "opacity-100 scale-100" : "pointer-events-none opacity-0 scale-95"
          }`}
          type="button"
          onClick={() => scrollToSection("top", false)}
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      </main>
      <Footer />
    </div>
  );
}
